// ClientApp/src/Components/DragAndDrop/filterFunctions.js
import { columnManager } from '../Common/ColumnManager';
import { getValueLabelFromFormInputs } from '../Common/CommonFunctions';

let _idCounter = 0;
export function generateId() {
    return `fn-${++_idCounter}-${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Serialization: FilterNode[] → filter string
// ---------------------------------------------------------------------------

export function serializeFilterTree(nodes) {
    if (!nodes || nodes.length === 0) return '';
    const filterType = nodes.find(n => n.kind === 'block')?.filterType || '';
    if (!filterType) return '';
    const body = nodes.map(serializeNode).join('');
    return `${filterType} ${body}`;
}

function serializeNode(node) {
    if (node.kind === 'logOp') return node.operator === 'AND' ? '&' : '|';
    if (node.kind === 'filter') return `${node.statId}${node.operator}${node.value}`;
    if (node.kind === 'block') return '(' + node.children.map(serializeNode).join('') + ')';
    return '';
}

// ---------------------------------------------------------------------------
// Deserialization: filter string → FilterNode[]
// ---------------------------------------------------------------------------

export async function deserializeFilterString(filterString) {
    if (!filterString) return [];

    let filterType, body;
    if (filterString.startsWith('ISW ')) { filterType = 'ISW'; body = filterString.slice(4); }
    else if (filterString.startsWith('IGW ')) { filterType = 'IGW'; body = filterString.slice(4); }
    else return [];

    const matchingParens = pairParentheses(body);
    const result = [];

    for (const parenPair of matchingParens) {
        const split = splitFilters(parenPair, body);
        const { leadingLogOp, block } = await buildBlockFromSplit(split, filterType);
        if (leadingLogOp) result.push(leadingLogOp);
        result.push(block);
    }
    return result;
}

async function buildBlockFromSplit(split, filterType) {
    const children = [];
    let leadingLogOp = null;
    let isFirstPart = true;

    for (const filterEntry of split.filters) {
        for (const part of filterEntry.split(';').filter(p => p.trim() !== '')) {
            if (part === 'AND' || part === 'OR') {
                const logOpNode = { kind: 'logOp', id: generateId(), operator: part };
                if (isFirstPart) { leadingLogOp = logOpNode; }
                else { children.push(logOpNode); }
            } else {
                const filterRegex = /(?<statId>[^>=< \n]+)(?<operator><>|>=|>|=|<=|<)(?<value>[^>=< \n]+)/;
                const match = filterRegex.exec(part);
                if (match) {
                    const statId = Number(match.groups.statId);
                    const column = await columnManager.getSingleColumnById(statId);
                    children.push({
                        kind: 'filter',
                        id: generateId(),
                        statId,
                        filterType,
                        operator: match.groups.operator,
                        value: match.groups.value,
                        granularity: column?.formInputs?.granularity ?? null,
                        filterText: column
                            ? column.title + match.groups.operator + getValueLabelFromFormInputs(column.formInputs, match.groups.value)
                            : part,
                    });
                }
            }
            isFirstPart = false;
        }
    }

    for (const nestedSplit of split.nestedFilters) {
        const { leadingLogOp: nestedLogOp, block: nestedBlock } = await buildBlockFromSplit(nestedSplit, filterType);
        if (nestedLogOp) children.push(nestedLogOp);
        children.push(nestedBlock);
    }

    return { leadingLogOp, block: { kind: 'block', id: generateId(), filterType, children } };
}

// ---------------------------------------------------------------------------
// Label derivation: FilterNode[] → human-readable string (Title or Field)
// ---------------------------------------------------------------------------

export async function deriveFilterLabel(nodes, prop) {
    if (!nodes || nodes.length === 0) return '';
    const filterType = nodes.find(n => n.kind === 'block')?.filterType || '';
    const parts = await Promise.all(nodes.map(node => deriveNodeLabel(node, prop)));
    const body = parts.join('');
    return filterType ? `${filterType} ${body}` : body;
}

async function deriveNodeLabel(node, prop) {
    if (node.kind === 'logOp') return node.operator === 'AND' ? '&' : '|';
    if (node.kind === 'filter') {
        const column = await columnManager.getSingleColumnById(node.statId);
        const valueLabel = getValueLabelFromFormInputs(column?.formInputs, node.value);
        const statLabel = prop === 'Field'
            ? (column?.field ?? String(node.statId))
            : (column?.title ?? String(node.statId));
        return `${statLabel}${node.operator}${valueLabel}`;
    }
    if (node.kind === 'block') {
        const childLabels = await Promise.all(node.children.map(c => deriveNodeLabel(c, prop)));
        return '(' + childLabels.join('') + ')';
    }
    return '';
}

// ---------------------------------------------------------------------------
// Tree mutation helpers (all return new arrays — no mutation)
// ---------------------------------------------------------------------------

export function insertIntoBlock(nodes, targetBlockId, filterNode) {
    if (!targetBlockId) {
        // Drop on outer container: wrap the filter in a new block unless top level node is a block in which case just return that block
        if (filterNode.kind === 'block')
            return [filterNode];
        const newBlock = {
            kind: 'block',
            id: generateId(),
            filterType: filterNode.filterType,
            children: [{ ...filterNode, id: generateId() }],
        };
        if (nodes.length > 0) {
            return [...nodes, { kind: 'logOp', id: generateId(), operator: 'AND' }, newBlock];
        }
        return [newBlock];
    }

    return nodes.map(node => {
        if (node.kind !== 'block') return node;
        if (node.id === targetBlockId) {
            const logOp = node.children.length > 0
                ? [{ kind: 'logOp', id: generateId(), operator: 'AND' }]
                : [];
            return { ...node, children: [...node.children, ...logOp, { ...filterNode, id: generateId() }] };
        }
        return { ...node, children: insertIntoBlock(node.children, targetBlockId, filterNode) };
    });
}

export function removeFromTree(nodes, targetId) {
    const result = [];
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node.id === targetId) {
            // Remove the adjacent logOp (prefer preceding, else following)
            if (result.length > 0 && result[result.length - 1].kind === 'logOp') {
                result.pop();
            } else if (i + 1 < nodes.length && nodes[i + 1].kind === 'logOp') {
                i++; // skip the next node (it's the logOp)
            }
            continue;
        }
        if (node.kind === 'block') {
            result.push({ ...node, children: removeFromTree(node.children, targetId) });
        } else {
            result.push(node);
        }
    }
    return result;
}

export function updateLogOpInTree(nodes, targetId, newOperator) {
    return nodes.map(node => {
        if (node.id === targetId) return { ...node, operator: newOperator };
        if (node.kind === 'block') return { ...node, children: updateLogOpInTree(node.children, targetId, newOperator) };
        return node;
    });
}

export function getGranularitiesFromNodes(nodes) {
    return nodes.flatMap(node => {
        if (node.kind === 'filter') return node.granularity != null ? [node.granularity] : [];
        if (node.kind === 'block') return getGranularitiesFromNodes(node.children);
        return [];
    });
}

export function deepCopyWithNewIds(nodes) {
    return nodes.map(node => {
        if (node.kind === 'logOp') return { ...node, id: generateId() };
        if (node.kind === 'filter') return { ...node, id: generateId() };
        if (node.kind === 'block') return { ...node, id: generateId(), children: deepCopyWithNewIds(node.children) };
        return node;
    });
}

export function handleAfterDrop() {
    Array.prototype.forEach.call(document.getElementsByClassName('dropZone'), d => d.classList.remove('available'));
    document.body.style.cursor = 'default';
}

// ---------------------------------------------------------------------------
// Parsing helpers (used by deserializeFilterString)
// ---------------------------------------------------------------------------

function pairParentheses(filterString) {
    const openIndices = getIndicesOfString('(', filterString);
    const closeIndices = getIndicesOfString(')', filterString);
    let matchingParens = [];
    let matchedOpenIndices = [];

    closeIndices.forEach(closeIdx => {
        const matchingOpen = Math.max(...openIndices.filter(o => o < closeIdx && !matchedOpenIndices.includes(o)));
        matchedOpenIndices.push(matchingOpen);
        matchingParens.push({ openIndex: matchingOpen, closeIndex: closeIdx, nestedParens: [] });
    });

    matchingParens.sort((a, b) => a.openIndex - b.openIndex);

    for (let i = matchingParens.length - 1; i > 0; i--) {
        for (let j = i - 1; j > -1; j--) {
            if (matchingParens[i].openIndex < matchingParens[j].closeIndex) {
                matchingParens[j].nestedParens.push(matchingParens[i]);
                matchingParens.splice(i, 1);
                break;
            }
        }
    }
    return matchingParens;
}

function splitFilters(parenIndices, filterString) {
    const curFilters = { filters: [], nestedFilters: [] };

    parenIndices.nestedParens.forEach(nested => {
        curFilters.nestedFilters.push(splitFilters(nested, filterString));
    });

    let filter = filterString.slice(parenIndices.openIndex + 1, parenIndices.closeIndex);
    if (['&', '|'].includes(filterString.charAt(parenIndices.openIndex - 1))) {
        filter = filterString.charAt(parenIndices.openIndex - 1) + filter;
    }

    const nestedRegex = /(([&|]\(|(\()).+\))/;
    while (nestedRegex.test(filter)) {
        filter = filter.replace(nestedRegex, '');
    }

    filter = filter.replaceAll('&', ';AND;').replaceAll('|', ';OR;').trim();
    filter = filter.charAt(0) === ';' ? filter.substring(1) : filter;
    curFilters.filters.push(filter);
    return curFilters;
}

function getIndicesOfString(searchStr, str) {
    const indices = [];
    let startIndex = 0;
    let index;
    while ((index = str.indexOf(searchStr, startIndex)) > -1) {
        indices.push(index);
        startIndex = index + 1;
    }
    return indices;
}
